import React from 'react';
import ApplicationSyncContextUpdater from '../ApplicationSyncContext/ApplicationSyncContextUpdater';
import AudioServerCommunicationContextProvider from '../NewSessionContext/AudioServerCommunicationContextProvider/AudioServerCommunicationContextProvider';
import EndSessionDialogWrapper from '../NewSessionContext/EndSessionDialogWrapper';
import InteractionConfirmationWrapper from '../NewSessionContext/InteractionConfirmationWrapper';
import SessionCardPinContextProvider from '../NewSessionContext/SessionCardPinContextProvider';
import useSessionCardPinContext from '../NewSessionContext/SessionCardPinContextProvider/useSessionCardPinContext';
import SessionComplianceContextProvider from '../NewSessionContext/SessionComplianceContextProvider';
import useSessionComplianceContext from '../NewSessionContext/SessionComplianceContextProvider/useSessionComplianceContext';
import SessionConnectionContextProvider from '../NewSessionContext/SessionConnectionContextProvider';
import SessionContextContextProvider from '../NewSessionContext/SessionContextContextProvider';
import useSessionContextContext from '../NewSessionContext/SessionContextContextProvider/useSessionContextContext';
import SessionDetectionContextProvider from '../NewSessionContext/SessionDetectionContextProvider';
import useSessionDetectionContext from '../NewSessionContext/SessionDetectionContextProvider/useSessionDetectionContext';
import SessionInfoContextProvider from '../NewSessionContext/SessionInfoContextProvider';
import useSessionInfoContext from '../NewSessionContext/SessionInfoContextProvider/useSessionInfoContext';
import SessionLanguageContextProvider from '../NewSessionContext/SessionLanguageContextProvider/SessionLanguageContextProvider';
import SessionManagerContextProvider from '../NewSessionContext/SessionManagerContextProvider';
import useSessionManagerContext from '../NewSessionContext/SessionManagerContextProvider/useSessionManagerContext';
import AudioCaptureUpdater from '../NewSessionContext/WebsocketConnectionProviders/AudioWebsocketApiConnectionContext/AudioCaptureUpdater';
import AudioWebsocketApiConnectionContextProvider from '../NewSessionContext/WebsocketConnectionProviders/AudioWebsocketApiConnectionContext/AudioWebsocketApiConnectionContextProvider';
import DetectionUpdater from '../NewSessionContext/WebsocketConnectionProviders/AudioWebsocketApiConnectionContext/DetectionUpdater';
import WebsocketApiConnectionContextProvider from '../NewSessionContext/WebsocketConnectionProviders/WebsocketApiConnectionContext/WebsocketApiConnectionContextProvider';
import { SessionContext } from '../SessionContextProvider/useSessionContext';

function Inner({ children }: { children: React.ReactNode }) {
  const { sessionId, session, setEndCause } = useSessionInfoContext();
  const { startSession, sessionLoading } = useSessionManagerContext();
  const { pinnedAt, pin, unpin } = useSessionCardPinContext();
  const compliance = useSessionComplianceContext();
  const { latestDetection } = useSessionDetectionContext();
  const { context, setContext } = useSessionContextContext();

  const value = React.useMemo(() => ({
    sessionId,
    session,
    sessionLoading,
    startSession,
    stopSession() { setEndCause('ManualSessionTermination'); },
    context,
    setContext,
    pinnedAt,
    pin,
    unpin,
    compliance,
    latestDetection,
  }), [sessionId, session, sessionLoading, startSession, setEndCause, context, setContext, pinnedAt, pin, unpin, compliance, latestDetection]);

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>

  );
}

/**
 * NewSessionContextProvider, the top level context provider for the live session
 * Explanation regarding the structure:
 * - First are the "Storage only context providers". They are only responsible for storing data and sharing it to multiple contexts.
 * - Underneath are "Complex context providers". They are context providers for which some information must be stored but cannot be split between the data and the logic.
 * - At the bottom are the "Context Updaters". They are responsible for the underlying logic of the context.
 */
export default function NewSessionContextProvider({ children }: { children: React.ReactNode }) {
  return (
    <WebsocketApiConnectionContextProvider>
      <SessionInfoContextProvider>
        <SessionLanguageContextProvider>
          <AudioWebsocketApiConnectionContextProvider>
            <AudioServerCommunicationContextProvider>
              <SessionContextContextProvider>
                <SessionManagerContextProvider>
                  <SessionCardPinContextProvider>
                    <SessionDetectionContextProvider>
                      <SessionConnectionContextProvider>
                        <SessionComplianceContextProvider>
                          {/* <Updaters> */}
                          <AudioCaptureUpdater>
                            <DetectionUpdater>
                              <InteractionConfirmationWrapper>
                                <Inner>
                                  <EndSessionDialogWrapper>
                                    <ApplicationSyncContextUpdater>
                                      {children}
                                    </ApplicationSyncContextUpdater>
                                  </EndSessionDialogWrapper>
                                </Inner>
                              </InteractionConfirmationWrapper>
                            </DetectionUpdater>
                          </AudioCaptureUpdater>
                          {/* </Updaters>  */}
                        </SessionComplianceContextProvider>
                      </SessionConnectionContextProvider>
                    </SessionDetectionContextProvider>
                  </SessionCardPinContextProvider>
                </SessionManagerContextProvider>
              </SessionContextContextProvider>
            </AudioServerCommunicationContextProvider>
          </AudioWebsocketApiConnectionContextProvider>
        </SessionLanguageContextProvider>
      </SessionInfoContextProvider>
    </WebsocketApiConnectionContextProvider>
  );
}
